import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { buildPublicUrl } from '../../../utils/url'
import { useVCardStore } from '../../../store/vCardStore'
import { useAuthStore } from '../../../store/authStore'
import { db } from '../../../services/firebase.js'
import { doc, deleteDoc } from 'firebase/firestore'
import LoadingSpinner from '../../../components/common/LoadingSpinner'
import Button from '../../../components/common/Button'
import toast from 'react-hot-toast'
import {
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ShareIcon,
  QrCodeIcon,
  DocumentDuplicateIcon,
  ChartBarIcon,
  PlusIcon
} from '@heroicons/react/24/outline'
import { QRCodeSVG } from 'qrcode.react'

function VCardListPage() {
  const { user } = useAuthStore()
  const { vCards, loading, error, fetchUserVCards, deleteVCard, createVCard } = useVCardStore()
  const [showQRModal, setShowQRModal] = useState(false)
  const [selectedVCard, setSelectedVCard] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [vCardToDelete, setVCardToDelete] = useState(null)

  useEffect(() => {
    if (user) {
      fetchUserVCards(user.uid)
    }
  }, [user, fetchUserVCards])

  useEffect(() => {
    if (error) {
      toast.error(error)
    }
  }, [error])

  const handleDelete = async (vCardId) => {
    try {
      console.log('Intentando eliminar Dkard con ID:', vCardId)
      
      // Eliminación directa sin depender del store
      await deleteDoc(doc(db, 'vcards', vCardId))
      
      console.log('Dkard eliminada de Firestore')
      
      // Actualizar la lista local
      await fetchUserVCards(user.uid)
      
      toast.success('Dkard eliminada exitosamente')
      setShowDeleteModal(false)
      setVCardToDelete(null)
    } catch (err) {
      console.error('Error completo al eliminar:', err)
      console.error('Mensaje:', err.message)
      console.error('Código:', err.code)
      
      // Mensaje de error más específico
      if (err.message?.includes('not defined')) {
        toast.error('Error: La función de Firebase no está disponible')
      } else if (err.code === 'permission-denied') {
        toast.error('Error: No tienes permisos para eliminar esta Dkard')
      } else {
        toast.error('Error al eliminar la Dkard: ' + err.message)
      }
    }
  }

  const createMinimalVCard = async () => {
    try {
      const minimalData = {
        name: 'Mi Nueva Dkard',
        position: 'Profesional',
        phone: '',
        email: '',
        bio: '',
        slug: `dkard-${Date.now()}`,
        public: false,
        templateId: 'template-1',
        theme: {
          primaryColor: '#2563eb',
          accentColor: '#64748b',
          font: 'Inter',
        },
        social: {},
        products: [],
        services: [],
        gallery: [],
        type: 'presentation',
        userId: user.uid,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }
      
      const newId = await createVCard(minimalData)
      toast.success('Dkard mínima creada exitosamente')
      navigate(`/editor/${newId}`)
    } catch (error) {
      console.error('Error al crear Dkard mínima:', error)
      toast.error('Error al crear Dkard mínima: ' + error.message)
    }
  }

  const handleShare = async (vCard) => {
    const shareUrl = buildPublicUrl(vCard.slug)
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Dkard de ${vCard.name}`,
          text: `Conoce la tarjeta digital de ${vCard.name}`,
          url: shareUrl,
        })
      } catch (err) {
        console.log('Share cancelled')
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl)
        toast.success('Enlace copiado al portapapeles')
      } catch (err) {
        toast.error('Error al copiar el enlace')
      }
    }
  }

  const showQRCode = (vCard) => {
    setSelectedVCard(vCard)
    setShowQRModal(true)
  }

  const duplicateVCard = (vCard) => {
    // This would typically create a copy of the vCard
    toast.success('Función de duplicado próximamente')
  }

  const getVCardStats = (vCard) => {
    // Mock stats - in real app this would come from analytics
    return {
      views: Math.floor(Math.random() * 1000) + 50,
      shares: Math.floor(Math.random() * 100) + 5,
      qrScans: Math.floor(Math.random() * 200) + 10,
      lastAccess: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toLocaleDateString()
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="large" />
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Mis Dkards</h1>
            <p className="text-gray-600">
              Gestiona tus tarjetas digitales profesionales
            </p>
          </div>
          <Link to="/editor/new">
            <Button variant="primary">
              <PlusIcon className="w-5 h-5 mr-2" />
              Nueva Dkard
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg">
              <DocumentDuplicateIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Dkards</p>
              <p className="text-2xl font-bold text-gray-900">{vCards.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg">
              <EyeIcon className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Vistas Totales</p>
              <p className="text-2xl font-bold text-gray-900">
                {vCards.reduce((total, vCard) => total + getVCardStats(vCard).views, 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center">
            <div className="p-3 bg-purple-100 rounded-lg">
              <ShareIcon className="w-6 h-6 text-purple-600" />
            </div>
            <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Compartidas</p>
              <p className="text-2xl font-bold text-gray-900">
                {vCards.reduce((total, vCard) => total + getVCardStats(vCard).shares, 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center">
            <div className="p-3 bg-orange-100 rounded-lg">
              <QrCodeIcon className="w-6 h-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Escaneos QR</p>
              <p className="text-2xl font-bold text-gray-900">
                {vCards.reduce((total, vCard) => total + getVCardStats(vCard).qrScans, 0)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* vCards List */}
      {vCards.length === 0 ? (
        <div className="text-center py-16">
          <DocumentDuplicateIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No tienes Dkards aún</h3>
          <p className="text-gray-600 mb-6">
            Crea tu primera tarjeta digital profesional para comenzar
          </p>
          <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
            <Button
              variant="outline"
              onClick={createMinimalVCard}
              className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              Crear Dkard Mínima
            </Button>
            <Link to="/editor/new">
              <Button variant="primary">
                <PlusIcon className="w-5 h-5 mr-2" />
                Crear Dkard Completa
              </Button>
            </Link>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {vCards.map((vCard) => {
            const stats = getVCardStats(vCard)
            return (
              <div key={vCard.id} className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
                {/* Preview */}
                <div className="p-6 border-b">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                      {vCard.name.charAt(0)}
                    </div>
                    <div className="ml-4 flex-1">
                      <h3 className="font-semibold text-gray-900">{vCard.name}</h3>
                      <p className="text-sm text-gray-600">{vCard.position}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      {vCard.public && (
                        <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                          Pública
                        </span>
                      )}
                      {!vCard.public && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded-full">
                          Privada
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Quick stats */}
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-gray-900">{stats.views}</p>
                      <p className="text-xs text-gray-600">Vistas</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-gray-900">{stats.shares}</p>
                      <p className="text-xs text-gray-600">Compartidas</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-gray-900">{stats.qrScans}</p>
                      <p className="text-xs text-gray-600">QR</p>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="p-4">
                  <div className="flex flex-wrap gap-2 mb-4">
                    <Link
                      to={buildPublicUrl(vCard.slug).replace(window.location.origin, '')}
                      target="_blank"
                      className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <EyeIcon className="w-4 h-4 mr-1" />
                      Ver
                    </Link>

                    <button
                      onClick={() => showQRCode(vCard)}
                      className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <QrCodeIcon className="w-4 h-4 mr-1" />
                      QR
                    </button>

                    <Link
                      to={`/editor/${vCard.id}`}
                      className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <PencilIcon className="w-4 h-4 mr-1" />
                      Editar
                    </Link>

                    <button
                      onClick={() => handleShare(vCard)}
                      className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <ShareIcon className="w-4 h-4 mr-1" />
                      Compartir
                    </button>

                    <button
                      onClick={() => {
                        setVCardToDelete(vCard)
                        setShowDeleteModal(true)
                      }}
                      className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-red-300 rounded-lg text-sm text-red-700 hover:bg-red-50 transition-colors"
                    >
                      <TrashIcon className="w-4 h-4 mr-1" />
                      Eliminar
                    </button>
                  </div>

                  <div className="flex space-x-2 text-xs text-gray-500">
                    <span>Último acceso: {stats.lastAccess}</span>
                    <span>•</span>
                    <span>Plantilla: {vCard.templateId?.replace('template-', '') || '1'}</span>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* QR Code Modal */}
      {showQRModal && selectedVCard && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-sm w-full p-6">
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Código QR - {selectedVCard.name}
              </h3>
              <p className="text-sm text-gray-600">
                Escanea para ver esta Dkard
              </p>
            </div>
            
            <div className="flex justify-center mb-6">
              <QRCodeSVG
                value={buildPublicUrl(selectedVCard.slug)}
                size={200}
                level="H"
                includeMargin={true}
              />
            </div>

            <div className="space-y-3">
              <button
                onClick={() => {
                  const canvas = document.createElement('canvas')
                  const ctx = canvas.getContext('2d')
                  const qrSize = 400
                  canvas.width = qrSize
                  canvas.height = qrSize + 100
                  
                  // White background
                  ctx.fillStyle = '#ffffff'
                  ctx.fillRect(0, 0, canvas.width, canvas.height)
                  
                  // QR code (you'd need to generate this properly)
                  ctx.fillStyle = '#000000'
                  ctx.font = '20px Arial'
                  ctx.textAlign = 'center'
                  ctx.fillText('QR Code', qrSize/2, qrSize + 30)
                  ctx.fillText(selectedVCard.name, qrSize/2, qrSize + 60)
                  
                  const link = document.createElement('a')
                  link.download = `qr-${selectedVCard.slug}.png`
                  link.href = canvas.toDataURL()
                  link.click()
                }}
                className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Descargar QR
              </button>
              
              <button
                onClick={() => setShowQRModal(false)}
                className="w-full py-2 px-4 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && vCardToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <TrashIcon className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Eliminar Dkard
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                ¿Estás seguro de que quieres eliminar la Dkard de "{vCardToDelete.name}"? 
                Esta acción no se puede deshacer.
              </p>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false)
                  setVCardToDelete(null)
                }}
                className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleDelete(vCardToDelete.id)}
                className="flex-1 py-2 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default VCardListPage
